/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { Initializer, Model, Config } from '@adobe-commerce/elsie/lib';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { getCookie } from '@/auth/lib/cookieUtils';
import { CustomerModel } from '@/auth/data/models';
import { verifyToken, getCustomerRolePermissions, getAdobeCommerceOptimizerData } from '@/auth/api';
import { events } from '@adobe-commerce/event-bus';
import { COOKIE_NAMES } from '@/auth/configs/cookieConfigs';

type ConfigProps = {
  langDefinitions?: Lang;
  authHeaderConfig: {
    header: string;
    tokenPrefix: string;
  };
  customerPermissionRoles?: boolean;
  adobeCommerceOptimizer?: boolean;
  models?: {
    CustomerModel?: Model<CustomerModel>;
  };
};

const _authenticated = new Config<boolean | undefined>(undefined);

export const initialize = new Initializer<ConfigProps>({
  init: async (config) => {
    const defaultConfig = {
      authHeaderConfig: {
        header: 'Authorization',
        tokenPrefix: 'Bearer',
      },
      customerPermissionRoles: false,
      adobeCommerceOptimizer: false,
    };

    const mergedConfig = { ...defaultConfig, ...config };

    initialize.config.setConfig(mergedConfig);

    const token = getCookie(COOKIE_NAMES.auth_dropin_user_token);

    const [authenticated] = await Promise.all([
      verifyToken(
        mergedConfig.authHeaderConfig.header,
        mergedConfig.authHeaderConfig.tokenPrefix
      ),
      mergedConfig.customerPermissionRoles && token
        ? getCustomerRolePermissions()
        : Promise.resolve(),
      mergedConfig.adobeCommerceOptimizer
        ? getAdobeCommerceOptimizerData()
        : Promise.resolve(),
    ]);

    _authenticated.setConfig(authenticated);
  },

  listeners: () => [
    events.on('authenticated', (next) => {
      const prev = _authenticated.getConfig();

      if (prev !== undefined && next !== prev) {
        _authenticated.setConfig(next);
        const { customerPermissionRoles, adobeCommerceOptimizer } = initialize.config.getConfig();
        if (customerPermissionRoles) {
          getCustomerRolePermissions();
        }
        if (adobeCommerceOptimizer) {
          getAdobeCommerceOptimizerData();
        }
      }
    }),
  ],
});

export const config = initialize.config;
